/**
 * MapSplit — full-width split layout with the map on the left (60%) and
 * a contact info panel on the right. More impactful than map-embed when
 * location is a key trust signal.
 *
 * Registered as: map / map-split
 */
import React, { useEffect, useState, useRef } from "react";
import { Map, MapMarker, MarkerContent, MarkerTooltip, MapControls } from "@repo/ui/map";
import type { SectionComponentProps } from "../../registry.js";
import { geocodeAddress } from "../../lib/geocode.js";
import { logConversionEvent } from "@repo/backend/firebase/analytics";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/** Sync with the platform's data-theme attribute for the CARTO basemap. */
function useDataTheme(): "light" | "dark" {
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof document === "undefined") return "light";
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "dark" || attr === "light") return attr;
    return typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const attr = document.documentElement.getAttribute("data-theme");
      if (attr === "dark" || attr === "light") setTheme(attr);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  return theme;
}

gsap.registerPlugin(ScrollTrigger);

interface MapSplitProps {
  headline?: string;
  address?: string;
  longitude?: number;
  latitude?: number;
  zoom?: number;
  markerLabel?: string;
  phone?: string;
  email?: string;
  directionsLabel?: string;
}

interface Coords { lng: number; lat: number }

export function MapSplit({ props }: SectionComponentProps) {
  const {
    headline = "Find Us",
    address,
    longitude,
    latitude,
    zoom = 14,
    markerLabel,
    phone,
    email,
    directionsLabel = "Get Directions",
  } = props as MapSplitProps;

  const hasExplicitCoords = longitude !== undefined && latitude !== undefined;
  const [coords, setCoords] = useState<Coords | null>(
    hasExplicitCoords ? { lng: longitude!, lat: latitude! } : null,
  );
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeFailed, setGeocodeFailed] = useState(false);

  const theme = useDataTheme();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (hasExplicitCoords || !address || coords || geocodeFailed) return;
    let cancelled = false;
    setGeocoding(true);
    geocodeAddress(address).then((result) => {
      if (cancelled) return;
      setGeocoding(false);
      if (result) setCoords({ lng: result.longitude, lat: result.latitude });
      else setGeocodeFailed(true);
    });
    return () => { cancelled = true; };
  }, [address, hasExplicitCoords, coords, geocodeFailed]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".ms-panel",
        { opacity: 0, x: 32 },
        {
          opacity: 1, x: 0, duration: 0.75, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 78%", toggleActions: "play none none none" },
        },
      );
    }, el);

    return () => ctx.revert();
  }, []);

  const tooltipLabel = markerLabel ?? address;
  const mapsUrl = address
    ? `https://www.google.com/maps/search/${encodeURIComponent(address)}`
    : coords
      ? `https://www.google.com/maps?q=${coords.lat},${coords.lng}`
      : "#";

  return (
    <section ref={sectionRef} className="bg-background" id="map">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[3fr_2fr] min-h-[420px]">
          {/* Map panel */}
          <div className="relative overflow-hidden min-h-[280px] lg:min-h-0 bg-muted">
            {coords ? (
              <Map center={[coords.lng, coords.lat]} zoom={zoom} interactive={true} theme={theme}>
                <MapMarker longitude={coords.lng} latitude={coords.lat}>
                  <MarkerContent>
                    <div className="w-4 h-4 rounded-full bg-primary border-2 border-white shadow-lg" />
                  </MarkerContent>
                  {tooltipLabel && <MarkerTooltip>{tooltipLabel}</MarkerTooltip>}
                </MapMarker>
                <MapControls showZoom position="bottom-right" />
              </Map>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[280px] text-muted-foreground text-sm">
                {geocoding ? (
                  <div className="text-center">
                    <div className="flex gap-1 justify-center mb-3">
                      {[0, 150, 300].map((d) => (
                        <span key={d} className="block w-2 h-2 rounded-full bg-muted-foreground/50 animate-pulse" style={{ animationDelay: `${d}ms` }} />
                      ))}
                    </div>
                    <p className="text-xs">Locating address…</p>
                  </div>
                ) : (
                  <div className="text-center p-8">
                    <div className="text-4xl mb-3">📍</div>
                    <p className="font-medium mb-1 text-foreground">No location set</p>
                    <p className="text-xs">Add <code className="bg-background px-1 rounded">address</code> to props</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Contact panel */}
          <div className="ms-panel flex flex-col justify-center gap-7 px-8 py-14 lg:py-16 bg-background border-l border-border">
            {headline && (
              <h2 className="text-2xl font-bold text-foreground">{headline}</h2>
            )}

            <div className="space-y-5">
              {address && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "var(--color-primary)" }}>Address</p>
                  <p className="text-foreground text-sm leading-relaxed whitespace-pre-line">{address}</p>
                </div>
              )}
              {phone && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "var(--color-primary)" }}>Phone</p>
                  <a href={`tel:${phone}`} onClick={() => logConversionEvent("click_call", { phone })} className="text-foreground hover:text-primary transition-colors text-sm font-medium">{phone}</a>
                </div>
              )}
              {email && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "var(--color-primary)" }}>Email</p>
                  <a href={`mailto:${email}`} className="text-foreground hover:text-primary transition-colors text-sm font-medium">{email}</a>
                </div>
              )}
            </div>

            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logConversionEvent("click_directions", { address: address ?? "" })}
              className="inline-flex items-center gap-2 self-start rounded-[var(--radius)] bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {directionsLabel}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
